import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { RootState, AppDispatch } from '../../store';
import {
  setExcelData, setStatus, setFileUploaded, setFileProcessed, resetState,
  toggleRowSelection, clearSelection, selectAllRows,
  setColumnFilter, setSortConfig, setFilterDropdown,
} from '../../store/userGenerationSlice';
import translations from '../../locales/translations';
import { matchesColumnFilter } from '../../utils/columnFilterValue';
import {
  generateInfraUsersStd, downloadSampleTemplate,
  validateUsersForDuplicates, preflightServers,
} from '../../services/api';
import { useDropzone } from 'react-dropzone';
import { GenerateUser } from '../../types';
import { generateUserColumns } from '../../tableConfig';
import { FaFilter, FaSpinner } from 'react-icons/fa';
import type { StatusColor, OperationState } from './UserGenerationTypes';
import { useNewGui } from '../../hooks/useNewGui';
import { FiUploadCloud, FiCheck, FiX } from 'react-icons/fi';

interface Props {
  selectedLicenseServerId: string;
  currentOperation: OperationState | null;
  updateStatus: (msg: string, color: StatusColor) => void;
  startPolling: (operationId: string) => void;
  resetOperation: () => void;
}

interface DuplicateValidation {
  isValidating: boolean;
  hasDuplicates: boolean;
  duplicates: Array<{
    index: number;
    email?: string;
    userId?: string;
    duplicates: Array<{ field: 'email' | 'userid'; existingUserId: number }>;
  }>;
  hasInputIssues: boolean;
  inputIssues: Array<{
    index: number;
    email?: string;
    userId?: string;
    issues: Array<{ field: string; message: string; value?: string }>;
  }>;
  summary?: {
    totalRecords: number;
    validRecords: number;
    duplicateRecords: number;
    recordsWithInputIssues: number;
    inputIssueCount: number;
  };
  validated: boolean;
  showDuplicateDetails: boolean;
}

const EMPTY_DUPLICATE_VALIDATION: DuplicateValidation = {
  isValidating: false,
  hasDuplicates: false,
  duplicates: [],
  hasInputIssues: false,
  inputIssues: [],
  validated: false,
  showDuplicateDetails: false,
};

function transformApiResponse(response: any[][]): Record<string, any>[] {
  if (response.length < 2) return [];
  const keys = response[0].map((k: any) => String(k).trim());
  return response.slice(1).map(row => {
    const obj: Record<string, any> = {};
    keys.forEach((key, i) => { obj[key] = row[i] !== undefined ? row[i] : null; });
    return obj;
  });
}

const dateFields = ['SetupDate', 'AccountExpiry', 'LastUpdated', 'LastLoginTime'];

const BulkGenerationTab: React.FC<Props> = ({
  selectedLicenseServerId, currentOperation, updateStatus, startPolling, resetOperation,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const useNew = useNewGui();
  const { fileUploaded, fileProcessed, selectedRows, columnFilters, sortConfig, filterDropdown } =
    useSelector((state: RootState) => state.userGeneration);

  const [users, setUsers] = useState<GenerateUser[]>([]);
  const [duplicateValidation, setDuplicateValidation] = useState<DuplicateValidation>(EMPTY_DUPLICATE_VALIDATION);
  const [preflightResult, setPreflightResult] = useState<{
    ok: boolean;
    warnings: Array<{ server: string; userExists: string[]; configExists: string[] }>;
    message: string;
  } | null>(null);
  const [preflightChecking, setPreflightChecking] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDuplicateValidation(prev => ({
      ...prev,
      hasDuplicates: false,
      duplicates: [],
      hasInputIssues: false,
      inputIssues: [],
      summary: undefined,
      validated: false,
      showDuplicateDetails: false,
    }));
  }, [selectedRows]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return users;
    return [...users].sort((a, b) => {
      const av = a[sortConfig.key] ?? '';
      const bv = b[sortConfig.key] ?? '';
      if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [users, sortConfig]);

  const filteredData = React.useMemo(() => (
    sortedData.filter(user =>
      generateUserColumns.every(col =>
        columnFilters[col.key as keyof GenerateUser]
          ? matchesColumnFilter(
              user[col.key as keyof GenerateUser],
              columnFilters[col.key as keyof GenerateUser]!
            )
          : true
      )
    )
  ), [sortedData, columnFilters]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<any[][]>(worksheet, { header: 1 });
      const transformed = transformApiResponse(json);
      const mapped: GenerateUser[] = transformed.map(u => ({
        sid: u['#'] || '', username: u['user-name'] || '', userid: u['user-id'] || undefined,
        usertype: u['user-type'] || undefined, details: u['user-details'] || undefined,
        primary_group: u['linux-group'] || undefined, sec_groups: u['sec-groups'] || undefined,
        email: u['email-id'] || '', mobileno: u['mobile'] || undefined,
        createdOn: u['created-on'] || undefined, enabled: u['status'] === 'enabled',
        system: u['system-type'] || undefined, mode: u['mode'] || undefined,
        initialPassword: u['initial-pwd'] || undefined, tools: u['tools'] || undefined,
        setupDate: u['setup-date'] || undefined, expiryDate: u['expiry-date'] || undefined,
        duration: u['duration'] || undefined, automation: u['automation'] || undefined,
        puttyStart: u['putty-start'] || undefined, puttyStatus: u['putty-status'] || undefined,
        puttyExpiry: u['putty-expiry'] || undefined, puttyDuration: u['putty-duration'] || undefined,
        puttyFQDN: u['putty-fqdn'] || undefined, puttyExtensionTill: u['putty-extension-till'] || undefined,
        puttyExtensionReason: u['putty-extension-reason'] || undefined,
        vncStart: u['vnc-start'] || undefined, vncStatus: u['vnc-status'] || undefined,
        vncExpiry: u['vnc-expiry'] || undefined, vncDuration: u['vnc-duration'] || undefined,
        vncFQDN: u['vnc-fqdn'] || undefined, vncExtensionTill: u['vnc-extension-till'] || undefined,
        vncExtensionReason: u['vnc-extension-reason'] || undefined,
        lastUpdate: u['last-update'] || undefined, address: u['user-address'] || undefined,
        remarks: u['remarks'] || undefined,
      }));
      setUsers(mapped);
      dispatch(setExcelData(json));
      dispatch(setStatus(translations.en.reviewingByUser));
      dispatch(setFileUploaded(true));
      dispatch(setFileProcessed(false));
    };
    reader.readAsArrayBuffer(file);
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  });

  const handleReset = useCallback(() => {
    setUsers([]);
    setDuplicateValidation(EMPTY_DUPLICATE_VALIDATION);
    setPreflightResult(null);
    dispatch(resetState());
    dispatch(setExcelData(null));
    dispatch(setStatus(''));
    dispatch(setFileUploaded(false));
    dispatch(setFileProcessed(false));
    dispatch(clearSelection());
    if (fileInputRef.current) fileInputRef.current.value = '';
    resetOperation();
    updateStatus('Page reset to initial state', 'blue');
  }, [dispatch, resetOperation, updateStatus]);

  const validateUsersBeforeGeneration = async () => {
    const selected = filteredData.filter(u => selectedRows.includes(u.sid));
    if (selected.length === 0) return null;
    setDuplicateValidation(prev => ({ ...prev, isValidating: true }));
    try {
      // Pass full selected records so backend can trim/skip empty rows and surface input issues.
      const result = await validateUsersForDuplicates(selected);
      const hasInputIssues = Boolean(result.hasInputIssues);
      const hasDuplicates = Boolean(result.hasDuplicates);
      const issueCount = result.summary?.inputIssueCount ?? result.inputIssueCount ?? 0;
      const duplicateCount = result.duplicates?.length ?? 0;

      setDuplicateValidation(prev => ({
        ...prev,
        isValidating: false,
        hasDuplicates,
        duplicates: result.duplicates || [],
        hasInputIssues,
        inputIssues: result.inputIssues || [],
        summary: result.summary,
        validated: true,
      }));

      if (hasInputIssues || hasDuplicates) {
        updateStatus(
          `Validation found ${issueCount} input issue(s) and ${duplicateCount} duplicate record(s). Please review before proceeding.`,
          'red',
        );
      } else {
        updateStatus(
          `Validation complete: ${result.summary?.validRecords ?? selected.length}/${result.summary?.totalRecords ?? selected.length} records are ready.`,
          'blue',
        );
      }
      return result;
    } catch {
      setDuplicateValidation(prev => ({ ...prev, isValidating: false, validated: true }));
      updateStatus('Failed to validate input records and users. Please try again.', 'red');
      return null;
    }
  };

  const handlePreflightServers = async () => {
    const selected = filteredData.filter(u => selectedRows.includes(u.sid));
    if (selected.length === 0) { updateStatus('No users selected. Select users first.', 'red'); return; }
    setPreflightChecking(true);
    setPreflightResult(null);
    try {
      const result = await preflightServers(selected.map(u => ({
        userid: u.userid, puttyFQDN: u.puttyFQDN, vncFQDN: u.vncFQDN, system: u.system,
      })));
      setPreflightResult(result);
      updateStatus(result.message, result.ok ? 'blue' : 'red');
    } catch {
      updateStatus('Pre-flight check failed. Ensure backend can SSH to target servers.', 'red');
    } finally {
      setPreflightChecking(false);
    }
  };

  const handleGenerateUsers = async () => {
    if (!selectedLicenseServerId) { updateStatus('Please select a target license server', 'red'); return; }
    const selected = filteredData.filter(u => selectedRows.includes(u.sid));
    if (selected.length === 0) { updateStatus('No users selected for generation', 'red'); return; }

    if (!duplicateValidation.validated) {
      const validationResult = await validateUsersBeforeGeneration();
      if (!validationResult) return;
      if (validationResult.hasDuplicates || validationResult.hasInputIssues) {
        updateStatus('Cannot proceed with generation. Please fix validation issues and validate again.', 'red');
        return;
      }
    }
    if (duplicateValidation.hasDuplicates || duplicateValidation.hasInputIssues) {
      updateStatus('Validation issues detected. Please resolve them before proceeding.', 'red');
      return;
    }

    const data = selected.map(user => {
      const row: { [key: string]: any } = {};
      generateUserColumns.forEach(col => { row[col.header] = user[col.key as keyof GenerateUser]; });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'InfraUsers');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const excelFile = new File(
      [new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })],
      'UserGen.xlsx',
    );

    try {
      resetOperation();
      updateStatus('Starting user generation operation...', 'orange');
      dispatch(setStatus(translations.en.genOprnWIP));
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('selectedLicenseServerId', selectedLicenseServerId);
      const response = await generateInfraUsersStd(formData);
      if (response.data?.operationId) {
        updateStatus('Operation started, monitoring progress...', 'orange');
        startPolling(response.data.operationId);
      } else {
        updateStatus(`Generate InfraUser completed! => ${JSON.stringify(response.data.message || response.data)}`, 'blue');
        dispatch(setStatus(translations.en.genOprnCompleted));
        dispatch(setFileProcessed(true));
      }
    } catch (error) {
      updateStatus(`Generate InfraUser operation FAILED: ${(error as Error).message}`, 'red');
      dispatch(setStatus('Operation Failed'));
    }
  };

  const hasBlockingIssues =
    duplicateValidation.hasDuplicates || duplicateValidation.hasInputIssues;

  // Upload -> Validate -> Confirm -> Run -> Results. Derived from existing
  // state rather than tracked separately, so it can't drift out of sync with
  // what the page is actually doing.
  const BULK_STEPS = ['Upload', 'Validate', 'Confirm', 'Run', 'Results'] as const;
  let bulkStepIndex = 0;
  if (fileUploaded) bulkStepIndex = 1;
  if (fileUploaded && duplicateValidation.validated && !hasBlockingIssues) bulkStepIndex = 2;
  if (currentOperation?.status === 'running') bulkStepIndex = 3;
  if (currentOperation?.status === 'completed') bulkStepIndex = 4;
  const bulkStepFailed = currentOperation?.status === 'failed';
  if (bulkStepFailed) bulkStepIndex = 3;

  // getInputProps() carries react-dropzone's own ref (needed for its
  // root-click-opens-file-picker behavior); a plain ref={fileInputRef}
  // after the spread was silently overwriting it, which is why clicking
  // the dropzone did nothing (drag-and-drop still worked since that's a
  // separate handler). Merge both refs instead of replacing one.
  const dropzoneInputProps = getInputProps();
  const dropzoneInternalRef = (dropzoneInputProps as { ref?: React.Ref<HTMLInputElement> }).ref;
  const mergedInputRef = (node: HTMLInputElement | null) => {
    fileInputRef.current = node;
    if (typeof dropzoneInternalRef === 'function') dropzoneInternalRef(node);
    else if (dropzoneInternalRef && 'current' in dropzoneInternalRef) {
      (dropzoneInternalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
  };

  return (
    <div className={useNew ? "font-plex" : undefined}>
      {useNew && (
        <div className="mb-6 flex items-center" aria-label="Bulk generation progress">
          {BULK_STEPS.map((label, i) => {
            const state: 'done' | 'current' | 'failed' | 'upcoming' =
              i < bulkStepIndex ? 'done'
              : i === bulkStepIndex ? (bulkStepFailed ? 'failed' : (i === BULK_STEPS.length - 1 ? 'done' : 'current'))
              : 'upcoming';
            const circleClass = {
              done: 'bg-success border-success text-white',
              current: 'bg-accent border-accent text-white',
              failed: 'bg-danger border-danger text-white',
              upcoming: 'bg-surface border-border-strong text-ink-secondary',
            }[state];
            const labelClass = state === 'upcoming' ? 'text-ink-secondary' : 'text-ink-primary font-medium';
            return (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold shrink-0 ${circleClass}`}>
                    {state === 'done' ? <FiCheck /> : state === 'failed' ? <FiX /> : i + 1}
                  </div>
                  <span className={`mt-1 text-[11px] ${labelClass}`}>{label}</span>
                </div>
                {i < BULK_STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mb-4 ${i < bulkStepIndex ? 'bg-success' : 'bg-border-subtle'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Drop zone + action buttons */}
      <div className="mb-4 flex items-start space-x-6">
        <div
          {...getRootProps()}
          className={useNew
            ? `w-80 h-32 border-2 border-dashed rounded-lg text-center p-4 transition-colors flex flex-col items-center justify-center gap-1 ${isDragActive ? 'border-accent bg-accent/5' : 'border-border-strong hover:border-accent bg-surface-raised'}`
            : "w-80 h-32 border-2 border-dashed border-gray-400 rounded-lg text-center p-4 hover:border-blue-400 transition-colors"
          }
        >
          <input {...dropzoneInputProps} ref={mergedInputRef} />
          {useNew && <FiUploadCloud className={`text-2xl ${isDragActive ? 'text-accent' : 'text-ink-secondary'}`} />}
          {isDragActive
            ? <p className={useNew ? "text-accent text-sm" : "text-blue-600"}>Drop the files here...</p>
            : <p className={useNew ? "text-ink-secondary text-sm" : "text-gray-600"}>Drag 'n' drop an .xlsx file, or click to select one</p>}
        </div>
        <div className="flex flex-col space-y-3">
          <button
            onClick={async () => {
              try {
                updateStatus('Downloading sample template...', 'blue');
                await downloadSampleTemplate();
                updateStatus('Sample template downloaded successfully!', 'blue');
              } catch {
                updateStatus('Failed to download sample template', 'red');
              }
            }}
            className={useNew
              ? "px-4 py-2 bg-accent text-white rounded-lg shadow-sm hover:bg-accent-hover transition whitespace-nowrap text-sm font-medium"
              : "px-4 py-2 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 transition whitespace-nowrap"
            }
          >
            Download Sample Template
          </button>
          <button
            onClick={handleReset}
            className={useNew
              ? "px-4 py-2 border border-border-strong text-ink-secondary rounded-lg hover:bg-surface-raised transition whitespace-nowrap text-sm font-medium"
              : "px-4 py-2 bg-gray-500 text-white rounded-lg shadow-sm hover:bg-gray-600 transition whitespace-nowrap"
            }
          >
            Reset
          </button>
        </div>
      </div>

      {/* Pre-submission input and user validation (from master handle-duplicate) */}
      {fileUploaded && selectedRows.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className={useNew ? "text-sm font-medium text-ink-primary" : "text-sm font-medium text-gray-700"}>Input Record and User Check</span>
            {!duplicateValidation.validated && !duplicateValidation.isValidating && (
              <button
                onClick={validateUsersBeforeGeneration}
                className={useNew
                  ? "px-3 py-1 bg-accent text-white text-sm rounded hover:bg-accent-hover transition"
                  : "px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
                }
              >
                Validate input records and users
              </button>
            )}
          </div>
          {duplicateValidation.isValidating && (
            <div className={useNew ? "flex items-center space-x-2 text-info" : "flex items-center space-x-2 text-blue-600"}>
              <FaSpinner className="animate-spin" />
              <span className="text-sm">Validating input records and users...</span>
            </div>
          )}
          {duplicateValidation.validated && (
            <div className={useNew
              ? `p-3 rounded-lg border ${hasBlockingIssues ? 'bg-danger/10 border-danger/30' : 'bg-success/10 border-success/30'}`
              : `p-3 rounded-lg ${hasBlockingIssues ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`
            }>
              {hasBlockingIssues ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className={useNew ? "text-danger font-medium" : "text-red-800 font-medium"}>
                      Validation found issues in {duplicateValidation.summary?.totalRecords ?? selectedRows.length} selected records
                    </span>
                    <button
                      onClick={() => setDuplicateValidation(prev => ({ ...prev, showDuplicateDetails: !prev.showDuplicateDetails }))}
                      className={useNew ? "text-danger hover:opacity-75 text-sm" : "text-red-600 hover:text-red-800 text-sm"}
                    >
                      {duplicateValidation.showDuplicateDetails ? 'Hide Details' : 'Show Details'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                    <div className={useNew ? "bg-surface border border-border-subtle rounded p-2" : "bg-white border rounded p-2"}>
                      <div className={useNew ? "text-ink-secondary" : "text-gray-500"}>Selected</div>
                      <div className={useNew ? "font-semibold text-ink-primary font-plexMono" : "font-semibold text-gray-900"}>{duplicateValidation.summary?.totalRecords ?? selectedRows.length}</div>
                    </div>
                    <div className={useNew ? "bg-success/10 border border-success/30 rounded p-2" : "bg-white border rounded p-2"}>
                      <div className={useNew ? "text-success" : "text-gray-500"}>Ready</div>
                      <div className={useNew ? "font-semibold text-success font-plexMono" : "font-semibold text-gray-900"}>{duplicateValidation.summary?.validRecords ?? 0}</div>
                    </div>
                    <div className={useNew ? "bg-warning/10 border border-warning/30 rounded p-2" : "bg-white border rounded p-2"}>
                      <div className={useNew ? "text-warning" : "text-gray-500"}>Input Issues</div>
                      <div className={useNew ? "font-semibold text-warning font-plexMono" : "font-semibold text-gray-900"}>
                        {duplicateValidation.summary?.inputIssueCount
                          ?? duplicateValidation.inputIssues.reduce((sum, item) => sum + item.issues.length, 0)}
                      </div>
                    </div>
                    <div className={useNew ? "bg-danger/10 border border-danger/30 rounded p-2" : "bg-white border rounded p-2"}>
                      <div className={useNew ? "text-danger" : "text-gray-500"}>Duplicates</div>
                      <div className={useNew ? "font-semibold text-danger font-plexMono" : "font-semibold text-gray-900"}>{duplicateValidation.duplicates.length}</div>
                    </div>
                  </div>
                  {duplicateValidation.showDuplicateDetails && (
                    <div className="space-y-2">
                      {duplicateValidation.inputIssues.map((item, index) => (
                        <div key={`issue-${index}`} className={useNew ? "text-sm text-danger bg-surface p-2 rounded border border-border-subtle" : "text-sm text-red-700 bg-white p-2 rounded border"}>
                          <div className="font-medium">Row {item.index + 1}: {item.email || item.userId || 'record'}</div>
                          <div className="text-xs mt-1">
                            {item.issues.map(issue =>
                              `${issue.field}: ${issue.message}${issue.value ? ` (${issue.value})` : ''}`
                            ).join('; ')}
                          </div>
                        </div>
                      ))}
                      {duplicateValidation.duplicates.map((dup, index) => (
                        <div key={`dup-${index}`} className={useNew ? "text-sm text-danger bg-surface p-2 rounded border border-border-subtle" : "text-sm text-red-700 bg-white p-2 rounded border"}>
                          <div className="font-medium">Row {dup.index + 1}: {dup.email || dup.userId}</div>
                          <div className="text-xs mt-1">
                            Duplicates: {dup.duplicates.map(d => `${d.field} (ID: ${d.existingUserId})`).join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={useNew ? "mt-3 text-sm text-danger" : "mt-3 text-sm text-red-600"}>
                    Fix input record issues and duplicate users before proceeding with generation.
                  </div>
                </div>
              ) : (
                <div className={useNew ? "text-success" : "text-green-800"}>
                  Validation complete: {duplicateValidation.summary?.validRecords ?? selectedRows.length}/{duplicateValidation.summary?.totalRecords ?? selectedRows.length} selected records are ready.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* User table */}
      <div className="overflow-x-auto">
        <table className={useNew ? "min-w-full bg-surface border border-border-subtle shadow-sm rounded-lg" : "min-w-full bg-white border border-gray-300 shadow-sm rounded-lg"}>
          <thead className={useNew ? "bg-surface-raised" : "bg-gray-200"}>
            <tr>
              <th className={useNew ? "py-2 px-4 border-b border-border-subtle" : "py-3 px-4 border-b"}>
                <input
                  type="checkbox"
                  onChange={e => dispatch(e.target.checked ? selectAllRows(filteredData.map(u => u.sid)) : clearSelection())}
                  checked={selectedRows.length === filteredData.length && selectedRows.length > 0}
                />
              </th>
              {generateUserColumns.map((col, i) => (
                <th key={i} className={useNew
                  ? "py-2 px-4 border-b border-border-subtle text-left text-xs font-semibold uppercase tracking-wider text-ink-secondary"
                  : "py-3 px-4 border-b text-left font-medium text-gray-800"
                }>
                  <div className="flex flex-col">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => {
                        const direction = sortConfig?.key === col.key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
                        dispatch(setSortConfig({ key: col.key as keyof GenerateUser, direction }));
                      }}
                    >
                      {col.header}
                      <FaFilter
                        className={useNew ? "ml-2 text-ink-secondary cursor-pointer" : "ml-2 text-gray-600 cursor-pointer"}
                        onClick={e => { e.stopPropagation(); dispatch(setFilterDropdown(filterDropdown === col.key ? null : col.key)); }}
                        style={{ width: '16px', height: '16px' }}
                      />
                    </div>
                    {filterDropdown === col.key && (
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder={`${translations.en.filterBy} ${col.header}`}
                          className={useNew ? "p-1 border border-border-strong rounded bg-surface text-ink-primary normal-case font-normal" : "p-1 border border-gray-300 rounded"}
                          value={columnFilters[col.key as keyof GenerateUser] || ''}
                          onChange={e => dispatch(setColumnFilter({ key: col.key as keyof GenerateUser, value: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map(user => (
              <tr key={user.sid} className={useNew ? "hover:bg-surface-raised border-b border-border-subtle" : "hover:bg-gray-50"}>
                <td className={useNew ? "py-2 px-4" : "py-2 px-4 border-b"}>
                  <input type="checkbox" checked={selectedRows.includes(user.sid)} onChange={() => dispatch(toggleRowSelection(user.sid))} />
                </td>
                {generateUserColumns.map((col, i) => {
                  const val = user[col.key as keyof GenerateUser];
                  let display: React.ReactNode = translations.en.empty;
                  if (val !== undefined && val !== null && val !== '') {
                    if (typeof val === 'boolean') {
                      display = val ? translations.en.yes : translations.en.no;
                    } else if (dateFields.includes(col.key) && !isNaN(Date.parse(String(val)))) {
                      display = new Date(String(val)).toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour12: false }).replace(',', '');
                    } else {
                      display = val as React.ReactNode;
                    }
                  }
                  return <td key={i} className={useNew ? "py-2 px-4 text-ink-primary" : "py-2 px-4 border-b text-gray-900"}>{display}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Generate buttons (shown after upload, before processing) */}
      {fileUploaded && !fileProcessed && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={handlePreflightServers}
            disabled={preflightChecking || selectedRows.length === 0}
            className={useNew
              ? "px-4 py-2 bg-warning text-white rounded-lg shadow-sm hover:opacity-90 disabled:opacity-50 transition text-sm font-medium"
              : "px-4 py-2 bg-amber-500 text-white rounded-lg shadow-sm hover:bg-amber-600 disabled:opacity-50 transition"
            }
          >
            {preflightChecking ? 'Checking…' : 'Check servers before generate'}
          </button>
          <button
            onClick={handleGenerateUsers}
            className={useNew
              ? "px-4 py-2 bg-success text-white rounded-lg shadow-sm hover:opacity-90 transition text-sm font-medium"
              : "px-4 py-2 bg-green-500 text-white rounded-lg shadow-sm hover:bg-green-600 transition"
            }
          >
            {translations.en.generateUsers}
          </button>
        </div>
      )}

      {/* Preflight warnings */}
      {preflightResult && !preflightResult.ok && preflightResult.warnings.length > 0 && (
        <div className={useNew ? "mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg" : "mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"}>
          <div className={useNew ? "font-medium text-warning mb-2" : "font-medium text-amber-800 mb-2"}>Pre-flight warnings (resolve before generating):</div>
          <ul className={useNew ? "list-disc list-inside text-sm text-warning space-y-1" : "list-disc list-inside text-sm text-amber-800 space-y-1"}>
            {preflightResult.warnings.map((w, i) => (
              <li key={i}>
                <strong>{w.server}</strong>
                {w.userExists.length > 0 && ` — User(s) already on server: ${w.userExists.join(', ')}. Remove or use different userid.`}
                {w.configExists.length > 0 && ` — Config file(s) in /opt/sms/cfg/users: ${w.configExists.join(', ')}. Remove leftover config from failed run.`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BulkGenerationTab;
